import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Link,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from "@mui/material";
import { 
  CheckCircle, 
  ErrorOutline, 
  Launch, 
  ContentCopy,
  Refresh
} from "@mui/icons-material";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { parseError, getApiDomain } from "../../../utils";

interface Props {
  editMode: boolean;
  initialData?: any;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
  onSubmitAttempt?: boolean;
  getToken: () => Promise<string>;
  companyId: string;
}

const steps = [
  'Authenticate Account',
  'Enter PIN', 
  'Test Connection'
];

export default function Office365IntegrationForm({
  editMode,
  initialData = {},
  onValidationChange,
  onDataChange,
  onSubmitAttempt = false,
  getToken,
  companyId
}: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [error, setError] = useState<string>("");
  
  // OAuth flow state
  const [deviceCode, setDeviceCode] = useState("");
  const [userCode, setUserCode] = useState("");
  const [verificationUri, setVerificationUri] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [authCompleted, setAuthCompleted] = useState(false);
  
  // Form data
  const [email, setEmail] = useState(initialData.email || "");
  const [oauthTokens, setOauthTokens] = useState(initialData.oauth_tokens || null);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  // Graph API is now mandatory for Office 365 integrations
  const [useGraphApi] = useState(true);

  // PIN input
  const [enteredPin, setEnteredPin] = useState("");

  // Validation - Graph API only
  const isValid = () => {
    if (editMode) {
      return email.trim() !== "" && oauthTokens;
    }
    return authCompleted && email.trim() !== "" && oauthTokens && connectionStatus?.graph_api;
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const initiateOAuth = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${getApiDomain()}/integrations/office365/initiate-auth`,
        { useGraphApi },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setDeviceCode(response.data.device_code);
        setUserCode(response.data.user_code);
        setVerificationUri(response.data.verification_uri);
        setVerificationMessage(response.data.message);
        setActiveStep(1);
      } else {
        setError(response.data.error || "Failed to initiate OAuth flow");
      }
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [getToken, useGraphApi]);

  const exchangeToken = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `${getApiDomain()}/integrations/office365/exchange-token`,
        {
          device_code: deviceCode,
          user_code: enteredPin || userCode
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setOauthTokens(response.data.tokens);
        // If email is null, we'll get it during the test connection step
        const userEmail = response.data.email || "";
        setEmail(userEmail);
        setAuthCompleted(true);
        setActiveStep(2);
      } else {
        setError(response.data.error || "Authentication failed");
      }
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [deviceCode, userCode, enteredPin, getToken]);

  const testConnection = useCallback(async () => {
    if (!oauthTokens) {
      setError("OAuth tokens are required");
      return;
    }
    
    if (!email || !email.trim()) {
      setError("Please enter your Office 365 email address");
      return;
    }
    
    let testEmail = email.trim();

    setTestingConnection(true);
    setError("");
    
    try {
      const token = await getToken();
      
      // Debug logging
      console.log("Testing connection with tokens:", oauthTokens);
      
      const response = await axios.post(
        `${getApiDomain()}/integrations/office365/test-connection`,
        {
          tokens: oauthTokens,
          email: testEmail,
          useGraphApi
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const testResult = response.data.connection_test;
        setConnectionStatus(testResult);

        // Update tokens if they were refreshed during test
        if (testResult.updated_tokens) {
          setOauthTokens(testResult.updated_tokens);
        }

        // Update email if it was retrieved during test
        if (testResult.email && !email) {
          setEmail(testResult.email);
        }

        // Check for errors - Graph API only
        if (!testResult.graph_api) {
          setError(testResult.error || "Connection test failed");
        }
      } else {
        setError(response.data.error || "Connection test failed");
        setConnectionStatus({ imap: false, smtp: false });
      }
    } catch (err: any) {
      setError(parseError(err));
      setConnectionStatus({ imap: false, smtp: false });
    } finally {
      setTestingConnection(false);
    }
  }, [oauthTokens, email, useGraphApi, getToken]);

  // Update parent component when data changes
  useEffect(() => {
    const formData = {
      email,
      oauth_tokens: oauthTokens,
      // IMAP settings for Office 365
      imapHost: "outlook.office365.com",
      imapPort: 993,
      smtpHost: "smtp.office365.com",
      smtpPort: 587,
      useSsl: true,
      useTls: true,
      useOAuth: true,
      useGraphApi,
      folder: "INBOX"
    };

    onDataChange(formData);
    onValidationChange(isValid());
  }, [email, oauthTokens, useGraphApi, onDataChange, onValidationChange]);

  // Initialize for edit mode
  useEffect(() => {
    if (editMode && initialData.oauth_tokens) {
      setOauthTokens(initialData.oauth_tokens);
      setEmail(initialData.email || "");
      setAuthCompleted(true);
      setActiveStep(2); // Skip to test connection step
    }
  }, [editMode, initialData]);

  const openAuthWindow = () => {
    window.open(verificationUri, '_blank', 'width=600,height=700');
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Office 365 Email Integration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Authenticate Account</StepLabel>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Click the button below to start the Office 365 authentication process.
              This will connect using Microsoft Graph API for reliable email sending and receiving.
            </Typography>

            <Button
              variant="contained"
              onClick={initiateOAuth}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Launch />}
              sx={{ mb: 2 }}
            >
              {loading ? "Starting..." : "Authenticate Account"}
            </Button>
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Enter PIN</StepLabel>
          <StepContent>
            {userCode && (
              <Box sx={{ mb: 2 }}>
                <Paper sx={{ p: 2, backgroundColor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Step 1:</strong> Click the link below to open the Microsoft authentication page:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Link
                      href={verificationUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {verificationUri}
                    </Link>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={openAuthWindow}
                      startIcon={<Launch />}
                    >
                      Open
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Step 2:</strong> Enter this code on the Microsoft page:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip 
                      label={userCode} 
                      color="primary" 
                      sx={{ fontSize: "1.1em", fontWeight: "bold", letterSpacing: "0.1em" }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => copyToClipboard(userCode)}
                      startIcon={<ContentCopy />}
                    >
                      Copy
                    </Button>
                  </Box>
                </Paper>
                
                <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
                  <strong>Step 3:</strong> After completing authentication on the Microsoft page, click the button below:
                </Typography>
                
                <Button
                  variant="contained"
                  onClick={exchangeToken}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                  sx={{ mr: 1 }}
                >
                  {loading ? "Checking..." : "Complete Authentication"}
                </Button>
              </Box>
            )}
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Test Connection</StepLabel>
          <StepContent>
            <TextField
              fullWidth
              label="Your Office 365 Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={onSubmitAttempt && !email.trim()}
              helperText={onSubmitAttempt && !email.trim() ? "Email address is required" : "Enter your Office 365 email address"}
              sx={{ mb: 2 }}
              type="email"
            />
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              Test the connection to ensure everything is working properly.
            </Typography>
            
            <Button
              variant="contained"
              onClick={testConnection}
              disabled={testingConnection || !oauthTokens}
              startIcon={testingConnection ? <CircularProgress size={20} /> : <Refresh />}
              sx={{ mb: 2 }}
            >
              {testingConnection ? "Testing..." : "Test Connection"}
            </Button>

            {connectionStatus && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Connection Status:</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    icon={connectionStatus.graph_api ? <CheckCircle /> : <ErrorOutline />}
                    label={`Graph API: ${connectionStatus.graph_api ? "Connected" : "Failed"}`}
                    color={connectionStatus.graph_api ? "primary" : "error"}
                    variant="outlined"
                  />
                </Box>

                {connectionStatus.graph_api && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Connection successful! Your Office 365 email is ready to use.
                  </Alert>
                )}
              </Box>
            )}
          </StepContent>
        </Step>
      </Stepper>

      {editMode && oauthTokens && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            This integration is already configured. You can test the connection or save to update settings.
          </Alert>
        </Box>
      )}
    </Box>
  );
}